import { useState, useEffect, useCallback, useRef } from "react";
import { logError } from "../utils/logging";
import { sttApi } from "../api/sttApi";
import { uploadFile } from "../utils/uploadHelper";

export const useJobs = (apiKeyId, page = 1, pageSize = 10) => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeJobs, setActiveJobs] = useState(0);
    const [maxConcurrent, setMaxConcurrent] = useState(5);
    const [totalJobs, setTotalJobs] = useState(0);
    const pollingIntervalRef = useRef(null);

    const fetchJobs = useCallback(async () => {
        // když chceš podporu "All keys", tak apiKeyId může být null -> NEreturnuj
        setLoading(true);
        try {
            const offset = (page - 1) * pageSize;
            const data = await sttApi.listUserJobs(null, apiKeyId ?? undefined, pageSize, offset);
            setJobs(data.jobs || []);
            setTotalJobs(data.total || 0);
        } catch (error) {
            logError(error, { context: "useJobs fetchJobs" });
        } finally {
            setLoading(false);
        }
    }, [apiKeyId, page, pageSize]);

    const fetchActiveJobsCount = useCallback(async () => {
        try {
            // count endpoint je "user scope", takže nepotřebuje apiKeyId
            const data = await sttApi.getActiveJobsCount();
            setActiveJobs(data.active_jobs);
            setMaxConcurrent(data.max_concurrent);
        } catch (error) {
            logError(error, { context: "useJobs fetchActiveJobsCount" });
        }
    }, []);

    // Initial fetch on mount or when dependencies change
    useEffect(() => {
        fetchJobs();
        fetchActiveJobsCount();
    }, [apiKeyId, fetchJobs, fetchActiveJobsCount]);

    // Polling: only poll if there are active jobs (pending or processing)
    // User can manually refresh if needed when there are no active jobs
    useEffect(() => {
        // Clear existing interval if any
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }

        // Only start polling if there are active jobs
        if (activeJobs > 0) {
            pollingIntervalRef.current = setInterval(() => {
                fetchActiveJobsCount();
                fetchJobs();
            }, 5000);
        }

        // Cleanup on unmount or when activeJobs changes
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, [activeJobs, fetchJobs, fetchActiveJobsCount]);

    const createJob = async (file, apiKeyId, onProgress = null, wordTimestamps = false, enableDiarization = false) => {
        try {
            // Use uploadFile helper which automatically chooses TUS for large files (>100MB)
            const response = await uploadFile(file, apiKeyId, onProgress, wordTimestamps, enableDiarization);
            fetchJobs();
            fetchActiveJobsCount();
            return response;
        } catch (error) {
            logError(error, { context: "useJobs createJob" });
            throw error;
        }
    };

    const cancelJob = async (jobId) => {
        try {
            await sttApi.cancelJob(jobId);
            fetchJobs();
            fetchActiveJobsCount();
        } catch (error) {
            logError(error, { context: "useJobs cancelJob" });
            throw error;
        }
    };

    const refetch = useCallback(async () => {
        // Manual refresh - fetch both jobs and active jobs count
        await Promise.all([fetchJobs(), fetchActiveJobsCount()]);
    }, [fetchJobs, fetchActiveJobsCount]);

    return { 
        jobs, 
        loading, 
        activeJobs, 
        maxConcurrent, 
        totalJobs,
        createJob, 
        cancelJob, 
        refetch
    };
};
